import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumberParam, StringParam, useQueryParam } from 'use-query-params'
import { Button, SearchComponent, Select, StartPage } from '../../components'
import { dedicatedServersService } from '../../api'
import { useUi } from '../../context/ui.context'
import { usePageTitle } from '../../context/navigationContext'
import useUpdateEffect from '../../hooks/useUpdateEffect'
import { extractErrorMessage } from '../../utils/error-extractor'
import { formatDate } from '../../utils/date-formatter'
import { DedicatedIpAssignmentDetails, PaginatedDedicatedIpAssignments } from './types'
import {
  FilterActions,
  Filters,
  FilterRow,
  FilterSelect,
  ListPage,
  SearchField,
  SearchRow,
} from './styles'

const DedicatedIpAssignments: FC = () => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [assignments, setAssignments] = useState<DedicatedIpAssignmentDetails[]>([])
  const [paginationData, setPaginationData] = useState<PaginatedDedicatedIpAssignments | null>(null)
  const [pageLoading, setPageLoading] = useState(true)

  const [page, setPage] = useQueryParam('page', NumberParam)
  const [pageSize] = useQueryParam('page_size', NumberParam)
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam)
  const [search, setSearch] = useQueryParam('search', StringParam)
  const [isPrimary, setIsPrimary] = useQueryParam('is_primary', StringParam)

  usePageTitle(String(t('DEDICATED_IP_ASSIGNMENTS')))

  const updateAssignments = useCallback(() => {
    setPageLoading(true)
    dedicatedServersService.getIpAssignments({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || undefined,
      search: search || undefined,
      is_primary: isPrimary || undefined,
    })
      .then((response) => {
        setAssignments(response.data.objects)
        setPaginationData(response.data)
      })
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('FAILED_TO_LOAD_DEDICATED_IPS'),
        })
      })
      .finally(() => setPageLoading(false))
  }, [isPrimary, ordering, page, pageSize, search, t, viewAlert])

  useEffect(() => {
    updateAssignments()
  }, [updateAssignments])

  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, search, isPrimary])

  const primaryOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'true', value: 'true', label: t('DEDICATED_PRIMARY_IPS') },
    { id: 'false', value: 'false', label: t('DEDICATED_SECONDARY_IPS') },
  ], [t])

  const columns = useMemo(() => [
    { title: t('DEDICATED_IP_ADDRESS'), key: 'name' },
    { title: t('DEDICATED_PRIMARY'), key: 'is_primary', sorted: true },
    { title: t('DEDICATED_SUBNET'), key: 'subnet', sorted: true },
    { title: t('DEDICATED_GATEWAY'), key: 'gateway' },
    { title: t('DEDICATED_SERVER'), key: 'server' },
    { title: t('DEDICATED_EXTERNAL_USER_ID'), key: 'external_user_id' },
    { title: t('DEDICATED_SYNCED_AT'), key: 'synced_at', sorted: true },
  ], [t])

  const rows = useMemo(() => assignments.map((assignment) => ({
    ...assignment,
    name: `${assignment.ip}${assignment.cidr !== null ? `/${assignment.cidr}` : ''}`,
    is_primary: assignment.is_primary ? t('YES') : t('NO'),
    subnet: assignment.subnet || '-',
    gateway: assignment.gateway || '-',
    server: assignment.server?.name || assignment.server?.servername || assignment.server?.hostname || (assignment.server?.external_id ? `#${assignment.server.external_id}` : '-'),
    external_user_id: assignment.server?.external_user_id ?? '-',
    synced_at: formatDate(assignment.synced_at),
    navigate: `/dedicated-servers/ips/${assignment.id}`,
  })), [assignments, t])

  const clearFilters = () => {
    setSearch(undefined)
    setIsPrimary(undefined)
    setOrdering(undefined)
    setPage(1)
  }

  const hasFilters = Boolean(search || isPrimary || ordering)

  return (
    <ListPage>
      <StartPage
      title={String(t('DEDICATED_IP_ASSIGNMENTS'))}
      noResourceText={String(t('NO_DEDICATED_IP_ASSIGNMENTS'))}
      haveResource={assignments.length > 0}
      pageLoading={pageLoading}
      withoutCreateResourceButton
      rightCustomCreateButton={(
        <Filters>
          <SearchRow>
            <SearchField>
              <SearchComponent />
            </SearchField>
            <FilterActions>
              <Button
                title={t('CLEAR_FILTERS')}
                onClick={clearFilters}
                variant="stroke"
                size="display"
                disabled={!hasFilters}
              />
            </FilterActions>
          </SearchRow>
          <FilterRow>
            <FilterSelect>
              <Select
                title={t('DEDICATED_IP_TYPE')}
                data={primaryOptions}
                selectedValue={isPrimary || ''}
                onChange={(value) => setIsPrimary(value || undefined)}
              />
            </FilterSelect>
          </FilterRow>
        </Filters>
      )}
      tableData={{
        columns,
        data: rows,
        paginationData,
        updateData: updateAssignments,
        hideCheckbox: true,
      }}
      />
    </ListPage>
  )
}

export default DedicatedIpAssignments
